import { JSX } from 'react';
import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import Helper from '../../../../Common/Helper';
import { PagedResponse } from "@iconics/core";
import { Ontology, OntologyNode, OntologyNode2, Solution } from './DataModels';
import { HeaderColumnAlignment } from '@monza/toolkit';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { ISolution } from '@monza/asset-controls';
import { Guid, TypeHelper } from '@iconics/core';
import { Button, ButtonBar, Card, DataGrid, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, RightButtons, Select, TickSymbol } from '@iconics/components';
import { ApiConstants } from '../../../../Providers.Api/ApiConstants';
import { DateTime } from 'luxon';

class AddOntologyNodeDialog extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;
    private cacheKey = 'c92f9864-0c3a-4601-abd7-796102202add';

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            gridData: { rows: [] },
            gridRefreshToken: Guid.new().toString(),
            ontologyNodes: [],
            selectedOntologyNodeId: '',
        };
    }

    public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): void
    {
        if (!prevProps.open && this.props.open)
        {
            this.handleOpen();
        }
        if (prevProps.open && !this.props.open)
        {
            this.handleClose();
        }
        if (this.props.open)
        {
            const prevCheckedSolutionId = this.checkedSolutionId(prevState);
            const checkedSolutionId = this.checkedSolutionId(this.state);
            if (prevCheckedSolutionId != checkedSolutionId)
            {
                this.handleSolutionCheck(checkedSolutionId);
            }
        }
    }

    private checkedSolutionId(state: IState): string
    {
        const checkedSolutionId = (state.gridData.checkedIds ?? [])[0] ?? '';
        return checkedSolutionId;
    }

    private handleOpen(): void
    {
        this.setState({
            gridRefreshToken: Guid.new().toString(),
            ontologyNodes: [],
            selectedOntologyNodeId: '',
        });
    }

    private handleClose(): void
    {
        this.setState({
            gridRefreshToken: Guid.new().toString(),
            ontologyNodes: [],
            selectedOntologyNodeId: '',
        });
    }

    private async handleRowsRequest(request: IGridRowsRequest<ISolutionAndOntology>): Promise<IGridRowsResponse<ISolutionAndOntology>>
    {
        if (!this.props.open)
        {
            return { rows: [], nextSkipToken: '' };
        }

        const nodesOntologyNodes = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.get<PagedResponse<OntologyNode[]>>({
            tenancy: appContext().tenantId,
            nodeId: this.props.nodeId,
        });
        const nodesOntologyNodeIds = nodesOntologyNodes.value.map(i => i.OntologyNode_ID);

        const solutions = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.get<PagedResponse<Solution[]>>({
            select: Solution,
            tenancy: appContext().tenantId,
            top: ApiConstants.largeODataTop,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const ontologyIds = solutions.value.map(i => i.Ontology_ID).distinct(i => i);
        const includedOntologyIds: string[] = [];

        for (const ontologyId of ontologyIds)
        {
            const ontologyNodeOptions = {
                tenancy: appContext().tenantId,
                id: ontologyId,
            };

            const ontologyNodes = await appContext().cache.lazyGet(
                `v2.core.byTenancy.ontologies.byId.nodes.get_${JSON.stringify(ontologyNodeOptions)}_${this.cacheKey}`,
                DateTime.now().plus({ seconds: 60 }),
                () => appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<OntologyNode2[]>>(ontologyNodeOptions));

            const ontologyNodeIds = ontologyNodes.value.map(i => i.Id);
            const excludeOntologyId = ontologyNodeIds.some(i => nodesOntologyNodeIds.includes(i));

            if (!excludeOntologyId)
            {
                includedOntologyIds.push(ontologyId);
            }
        }

        const filteredSolutions = solutions.value.filter(i => includedOntologyIds.includes(i.Ontology_ID));
        const filteredOntologyIdsString = includedOntologyIds.map(i => `'${i}'`).join(',');

        const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<Ontology[]>>({
            select: Ontology,
            tenancy: appContext().tenantId,
            top: ApiConstants.largeODataTop,
            filter: `Id in (${filteredOntologyIdsString})`,
        });

        const solutionsRows = filteredSolutions.map(solution =>
        {
            const ontology = ontologies.value.find(ontology => ontology.Id == solution.Ontology_ID);

            return {
                id: solution.Id,
                name: solution.Name,
                displayName: this.languageValuesHelper.fromString(solution.DisplayName),
                isEnabled: TypeHelper.toBoolean(solution.IsEnabled) ?? false,
                ontologyId: solution.Ontology_ID,
                ontologyName: ontology?.Name ?? '',
                ontologyDisplayName: this.languageValuesHelper.fromString(ontology?.DisplayName ?? ''),
                ontologyIsEnabled: TypeHelper.toBoolean(ontology?.IsEnabled ?? null) ?? false,

            } satisfies ISolutionAndOntology;
        });

        return { rows: solutionsRows, nextSkipToken: '' };
    }

    private async handleSolutionCheck(solutionId: string): Promise<void>
    {
        const checkedSolution = this.state.gridData.rows.find(i => i.id == solutionId);
        if (!checkedSolution)
        {
            this.setState({ ontologyNodes: [], selectedOntologyNodeId: '' });
            return;
        }

        this.loading(async () =>
        {
            const ontologyNodesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.availableOntologyNodes.get<PagedResponse<OntologyNode2[]>>({
                tenancy: appContext().tenantId,
                nodeId: this.props.nodeId,
                select: OntologyNode2,
                filter: `Ontology_ID eq '${checkedSolution.ontologyId}'`,
                top: ApiConstants.largeODataTop,
            });

            const ontologyNodes = ontologyNodesData.value
                .sort((a, b) => a.Name.localeCompare(b.Name))
                .map(i => ({
                    id: i.Id,
                    name: i.Name,
                    isEnabled: TypeHelper.toBoolean(i.IsEnabled) ?? false,
                } as IOntologyNode));

            this.setState({ ontologyNodes: ontologyNodes, selectedOntologyNodeId: '' });
        });
    }

    private handleCancel(): void
    {
        this.props.onClose();
    }

    private async handleSave(): Promise<void>
    {
        const checkedSolutionId = this.checkedSolutionId(this.state)
        if (!checkedSolutionId || !this.state.selectedOntologyNodeId)
        {
            return;
        }

        await this.loading(async () =>
        {
            // endpoint will bomb if solution is already added to the hierarchy node
            try
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.solutions.bySolutionId.post({
                    nodeId: this.props.nodeId,
                    tenancy: appContext().tenantId,
                    solutionId: checkedSolutionId,
                    suppressErrorPopup: true,
                });
            }
            catch { }

            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.ontologyNodes.byOntologyNodeId.post({
                nodeId: this.props.nodeId,
                tenancy: appContext().tenantId,
                ontologyNodeId: this.state.selectedOntologyNodeId,
            });

            this.props.onSave();
        });
    }

    public render(): JSX.Element
    {
        return (
            <Dialog
                isOpen={this.props.open}
                onOpenChange={() => this.handleCancel()}
            >
                <DialogContent>
                    <Card
                        title={this.labels.Platform_Node_AddOntologyNodeTitle}
                        subTitle={this.labels.Platform_Node_AddOntologyNodeSubTitle}
                        details={this.labels.Platform_Node_AddOntologyNodeDetails}
                    >
                        <GridContainer>
                            <GridItem>
                                <DataGrid
                                    columns={[
                                        {
                                            columnId: Helper.nameOf<Solution>('Name'),
                                            title: this.labels.Solution_Name,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Solution_EntityName, i.title),
                                            getCellValue: i => i.name,
                                            columnWidth: 20,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<Solution>('DisplayName'),
                                            title: this.labels.Solution_DisplayName,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Solution_EntityName, i.title),
                                            getCellValue: i => this.languageValuesHelper.getValue(i.displayName),
                                            columnWidth: 20,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<Solution>('IsEnabled'),
                                            title: this.labels.Solution_IsEnabled,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Solution_EntityName, i.title),
                                            getCellValue: i => <TickSymbol value={i.isEnabled} />,
                                            columnWidth: 10,
                                            sortable: false,
                                        },
                                        {
                                            columnId: Helper.nameOf<ISolutionAndOntology>('ontologyName'),
                                            title: this.labels.Ontology_Name,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Ontology_EntityName, i.title),
                                            getCellValue: i => i.ontologyName,
                                            columnWidth: 20,
                                            sortable: false,
                                        },
                                        {
                                            columnId: Helper.nameOf<ISolutionAndOntology>('ontologyDisplayName'),
                                            title: this.labels.Ontology_DisplayName,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Ontology_EntityName, i.title),
                                            getCellValue: i => this.languageValuesHelper.getValue(i.ontologyDisplayName),
                                            columnWidth: 20,
                                            sortable: false,
                                        },
                                        {
                                            columnId: Helper.nameOf<ISolutionAndOntology>('ontologyIsEnabled'),
                                            title: this.labels.Ontology_IsEnabled,
                                            headerTemplate: (i) => this.renderHeader(this.labels.Ontology_EntityName, i.title),
                                            getCellValue: i => <TickSymbol value={i.ontologyIsEnabled} />,
                                            columnWidth: 10,
                                            sortable: false,
                                        },
                                    ]}
                                    data={this.state.gridData}
                                    onChange={i => this.setState({ gridData: i })}
                                    rowId={i => i.id}
                                    onRowsRequest={i => this.handleRowsRequest(i)}
                                    refreshToken={this.state.gridRefreshToken}
                                    showCheckBoxes={true}
                                    allowOnlyOneCheckBox={true}
                                />
                            </GridItem>
                            <GridItem>
                                <Select
                                    label={this.labels.Node_OntologyNode}
                                    placeholder={this.labels.Node_OntologyNode}
                                    items={this.state.ontologyNodes.map(i => ({ label: i.name, value: i.id }))}
                                    value={this.state.selectedOntologyNodeId == '' ? [] : [this.state.selectedOntologyNodeId]}
                                    onSelect={e => this.setState({ selectedOntologyNodeId: e.value })}
                                    disabled={(this.state.gridData.checkedIds ?? []).length == 0}

                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.state.selectedOntologyNodeId}
                            >
                                {this.labels.Platform_CommonAction_Add}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        )
    }

    private renderHeader(entityName: string, fieldName: string): JSX.Element
    {
        return (
            <div>
                <div>{entityName}</div>
                <div>{fieldName}</div>
            </div>
        );
    }
}

export default AddOntologyNodeDialog;

export interface IProps
{
    open: boolean;
    nodeId: number;
    onClose: () => void;
    onSave: () => void;
}

export interface IState
{
    gridData: IGridData<ISolutionAndOntology>;
    gridRefreshToken: string;
    ontologyNodes: IOntologyNode[];
    selectedOntologyNodeId: string;
}

export interface ISolutionAndOntology
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    ontologyId: string;
    ontologyName: string;
    ontologyDisplayName: ILanguageValues;
    ontologyIsEnabled: boolean;
}

export interface IOntologyNode
{
    id: string;
    name: string;
    isEnabled: boolean;
}
